import { useEffect, useRef, useState } from 'react'

/**
 * Keeps a loading state visible for a consistent minimum window so navigations
 * always show the same loading transition — even when the underlying data
 * resolves instantly from cache and no network call is made.
 *
 * The inbox fetches with `network-only` and reads several queries from urql's
 * cache. On a cold load the loading UI shows for a few hundred ms; on a warm
 * revisit the same data is already cached, so the loading flag flips
 * true→false in a few ms (a "blink") or never turns true at all. Either way
 * the transition is inconsistent with the first load.
 *
 * This hook forces the loading UI to stay visible for at least `minDurationMs`
 * starting from mount and restarting whenever `triggerKey` changes (e.g. a new
 * thread is selected). It also stays visible as long as the real `loading`
 * flag is true. The loading state clears only once both conditions are met:
 * the real data has finished loading AND the minimum window has elapsed.
 *
 * Caveat: `triggerKey` is meant for coarse navigation identifiers (a selected
 * thread/location id) that change on discrete user actions. Do NOT wire it to a
 * value that changes rapidly (e.g. a live search query bound to keystrokes) —
 * each change restarts the minimum window, so the loading UI would replay on
 * every change and never settle. Debounce such values in the caller before
 * passing them here.
 *
 * @param loading  the real loading flag from the data source
 * @param options.minDurationMs  minimum time the loading state stays visible (default 400ms)
 * @param options.triggerKey  coarse navigation id; when it changes, the minimum window restarts (e.g. selected thread id). Must not change rapidly — see caveat above.
 * @returns a loading flag that respects the minimum-visible floor
 */
export function useMinimumLoading(
  loading: boolean,
  options: { minDurationMs?: number; triggerKey?: string | number } = {},
): boolean {
  const { minDurationMs = 400, triggerKey } = options

  const [showLoading, setShowLoading] = useState(true)
  // When the current minimum-visible window started. Reset on mount and on each
  // triggerKey change so every navigation gets the full window.
  const windowStartedAtRef = useRef<number | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Restart the window whenever the trigger key changes. This covers switching
  // threads: the cache may have the new thread ready instantly, but we still
  // want the loading transition to play.
  useEffect(() => {
    windowStartedAtRef.current = Date.now()
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setShowLoading(true)
  }, [triggerKey])

  useEffect(() => {
    // Stay in the loading state while real loading is in flight — don't schedule
    // a hide until the data has actually settled.
    if (loading) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      setShowLoading(true)
      return
    }

    // Real loading finished: hide once the minimum window has elapsed.
    const elapsed = Date.now() - (windowStartedAtRef.current ?? Date.now())
    const remaining = Math.max(0, minDurationMs - elapsed)

    if (remaining === 0) {
      setShowLoading(false)
      return
    }

    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null
      setShowLoading(false)
    }, remaining)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [loading, minDurationMs, triggerKey])

  return showLoading
}
